import { memo } from 'react';
import { Modal } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle, Building2, Grid3X3, User, Users } from 'lucide-react';
import { ASSIGNEE_TYPES } from '../constants';
import { assignmentAccessLevel, userInitials } from '../utils/shareDashboardAssignmentUtils';
import DashboardButton from './DashboardButton';

const ShareConfirmationStep = memo(function ShareConfirmationStep({
  dashboardName,
  stagedCounts,
  normalizedAssignments,
  renderConfirmationCanDo,
  onBack,
  onConfirm,
  confirming,
}) {
  const { t } = useTranslation('dashboards');

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-content">
            <Grid3X3 size={16} aria-hidden />
            <span className="truncate">{dashboardName || t('common.dashboardNameFallback')}</span>
          </div>
          {(stagedCounts.users > 0 || stagedCounts.departments > 0) && (
            <div className="flex flex-wrap items-center justify-start gap-3">
              {stagedCounts.users > 0 ? (
                <div className="flex min-w-0 items-center gap-3 text-xs text-content-secondary">
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F2F2F2] dark:bg-gray-800"
                    aria-hidden
                  >
                    <User className="h-4 w-4 text-[#2173FF] dark:text-blue-400" strokeWidth={2} />
                  </div>
                  <span>{t('share.modal.recipientUsers', { count: stagedCounts.users })}</span>
                </div>
              ) : null}
              {stagedCounts.departments > 0 ? (
                <div className="flex min-w-0 items-center gap-3 text-xs text-content-secondary">
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F2F2F2] dark:bg-gray-800"
                    aria-hidden
                  >
                    <Building2 className="h-4 w-4 text-[#2173FF] dark:text-blue-400" strokeWidth={2} />
                  </div>
                  <span>{t('share.modal.recipientDepartments', { count: stagedCounts.departments })}</span>
                </div>
              ) : null}
            </div>
          )}
        </div>
        <div className="space-y-1">
          <p className="text-xs font-semibold text-content">{t('share.modal.recipientsPermissions')}</p>
          <div className="scrollbar-aimsos-auto-sm max-h-64 overflow-y-auto overflow-x-hidden">
            <div className="mb-3 grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,1fr)] items-center gap-x-4 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-content-muted">
              <span className="text-left">{t('share.confirmation.headers.name')}</span>
              <span className="text-center">{t('share.confirmation.headers.canDo')}</span>
              <span className="text-right">{t('share.confirmation.headers.accessLevel')}</span>
            </div>
            <div className="flex flex-col gap-5">
              {normalizedAssignments.map((item) => (
                <div
                  key={`confirm-${item.id}`}
                  className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,1fr)] items-center gap-x-4 px-0.5 text-xs"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-600 text-[10px] font-semibold text-white dark:bg-teal-500">
                      {item.type === ASSIGNEE_TYPES.DEPARTMENT ? <Users size={10} /> : userInitials(item.name)}
                    </span>
                    <span className="truncate font-medium text-content">{item.name}</span>
                  </div>
                  <div className="flex min-w-0 justify-center">{renderConfirmationCanDo(item)}</div>
                  <span className="min-w-0 break-words text-right text-content-secondary">
                    {assignmentAccessLevel(item, t).join(' · ') || '-'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-400/80 dark:bg-amber-950/35">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-800 dark:text-amber-400" aria-hidden />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-amber-950 dark:text-amber-100">
            {t('share.confirmation.warningTitle')}
          </p>
          <p className="text-xs text-amber-900 dark:text-amber-200">{t('share.confirmation.warningBody')}</p>
        </div>
      </div>
      <Modal.Footer className="!px-0 !pb-0">
        <DashboardButton variant="secondary" onClick={onBack}>
          {t('share.confirmation.backButton')}
        </DashboardButton>
        <DashboardButton onClick={onConfirm} loading={confirming}>
          {t('share.confirmation.confirmButton')}
        </DashboardButton>
      </Modal.Footer>
    </div>
  );
});

export default ShareConfirmationStep;
